import { EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { TableColumnsType } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import {
  createCountry,
  listCountries,
  setCountryActive,
  updateCountry,
} from '../api/countries';
import type { Country } from '../api/countries';
import { ApiError } from '../api/errors';
import { PermissionCodes } from '../auth/permissions';
import { useAuth } from '../auth/useAuth';

/** Giá trị của biểu mẫu thêm hoặc sửa quốc gia. */
interface CountryFormValues {
  readonly code: string;
  readonly codeAlpha3: string | null;
  readonly name: string;
  readonly nameEn: string;
  readonly phoneCode: string | null;
  readonly defaultCurrencyCode: string | null;
  readonly sortOrder: number;
}

/** Dữ liệu của một trang đã nạp xong, kèm khóa nhận dạng lần nạp đó. */
interface LoadedPage {
  readonly key: string;
  readonly items: readonly Country[];
  readonly totalCount: number;
}

/** Số bản ghi mỗi trang. Khớp với mặc định của máy chủ. */
const pageSize = 25;

/**
 * Trang danh mục quốc gia.
 *
 * Quốc gia không phải một nhãn để hiển thị: nó đi vào tờ khai hải quan, giấy chứng nhận xuất xứ, và
 * việc xác định nhà cung cấp có phải nhà thầu nước ngoài. Vì vậy các ô nhập mã đều bị máy chủ bắt đúng
 * chuẩn ISO, và giao diện nói rõ khuôn cần nhập ngay dưới mỗi ô thay vì để người dùng đoán.
 */
export function CountryPage(): ReactNode {
  const { hasPermission } = useAuth();

  const canManage = hasPermission(PermissionCodes.CountryManage);

  const [form] = Form.useForm<CountryFormValues>();

  const [pageNumber, setPageNumber] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const [loaded, setLoaded] = useState<LoadedPage | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [editingCountry, setEditingCountry] = useState<Country | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const requestKey = useMemo(
    () => JSON.stringify({ searchTerm, includeInactive, pageNumber, reloadToken }),
    [searchTerm, includeInactive, pageNumber, reloadToken],
  );

  const isLoading = loaded?.key !== requestKey && errorMessage === null;

  const reload = useCallback((): void => {
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const page = await listCountries({
          searchTerm,
          includeInactive,
          pageNumber,
          pageSize,
        });

        if (controller.signal.aborted) {
          return;
        }

        setErrorMessage(null);
        setLoaded({ key: requestKey, items: page.items, totalCount: page.totalCount });
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError
            ? error.message
            : 'Không kết nối được tới máy chủ. Kiểm tra lại đường mạng.',
        );
      }
    })();

    return () => {
      controller.abort();
    };
  }, [requestKey, searchTerm, includeInactive, pageNumber]);

  const items = useMemo<readonly Country[]>(() => loaded?.items ?? [], [loaded]);
  const totalCount = loaded?.totalCount ?? 0;

  function openCreateForm(): void {
    setEditingCountry(null);
    form.setFieldsValue({
      code: '',
      codeAlpha3: null,
      name: '',
      nameEn: '',
      phoneCode: null,
      defaultCurrencyCode: null,
      sortOrder: 900,
    });
    setIsFormOpen(true);
  }

  function openEditForm(country: Country): void {
    setEditingCountry(country);
    form.setFieldsValue({
      code: country.code,
      codeAlpha3: country.codeAlpha3,
      name: country.name,
      nameEn: country.nameEn,
      phoneCode: country.phoneCode,
      defaultCurrencyCode: country.defaultCurrencyCode,
      sortOrder: country.sortOrder,
    });
    setIsFormOpen(true);
  }

  async function handleSubmit(values: CountryFormValues): Promise<void> {
    setIsSaving(true);

    try {
      if (editingCountry === null) {
        await createCountry({
          code: values.code,
          codeAlpha3: values.codeAlpha3,
          name: values.name,
          nameEn: values.nameEn,
          phoneCode: values.phoneCode,
          defaultCurrencyCode: values.defaultCurrencyCode,
          sortOrder: values.sortOrder,
        });

        void message.success('Đã thêm quốc gia.');
      } else {
        await updateCountry(editingCountry.id, {
          codeAlpha3: values.codeAlpha3,
          name: values.name,
          nameEn: values.nameEn,
          phoneCode: values.phoneCode,
          defaultCurrencyCode: values.defaultCurrencyCode,
          sortOrder: values.sortOrder,
        });

        void message.success('Đã lưu thay đổi.');
      }

      setIsFormOpen(false);
      reload();
    } catch (error) {
      void message.error(
        error instanceof ApiError ? error.message : 'Không lưu được. Thử lại sau.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  async function handleToggleActive(country: Country): Promise<void> {
    try {
      await setCountryActive(country.id, !country.isActive);
      reload();
    } catch (error) {
      void message.error(
        error instanceof ApiError ? error.message : 'Không đổi được trạng thái.',
      );
    }
  }

  const columns: TableColumnsType<Country> = [
    {
      title: 'Mã',
      dataIndex: 'code',
      width: 70,
      render: (code: string) => <Typography.Text strong>{code}</Typography.Text>,
    },
    {
      title: 'Mã 3 chữ',
      dataIndex: 'codeAlpha3',
      width: 90,
      render: (codeAlpha3: string | null) => codeAlpha3 ?? '—',
    },
    { title: 'Tên', dataIndex: 'name' },
    { title: 'Tên tiếng Anh', dataIndex: 'nameEn' },
    {
      title: 'Mã điện thoại',
      dataIndex: 'phoneCode',
      width: 120,
      render: (phoneCode: string | null) => phoneCode ?? '—',
    },
    {
      title: 'Tiền tệ',
      dataIndex: 'defaultCurrencyCode',
      width: 90,
      render: (currencyCode: string | null) => currencyCode ?? '—',
    },
    {
      title: 'Trạng thái',
      dataIndex: 'isActive',
      width: 110,
      render: (isActive: boolean) =>
        isActive ? <Tag color="green">Đang dùng</Tag> : <Tag>Đã ngừng</Tag>,
    },
    {
      title: '',
      key: 'actions',
      width: 150,
      render: (_value: unknown, country: Country) =>
        canManage ? (
          <Space size={4}>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                openEditForm(country);
              }}
            >
              Sửa
            </Button>

            <Button
              size="small"
              onClick={() => {
                void handleToggleActive(country);
              }}
            >
              {country.isActive ? 'Ngừng' : 'Dùng lại'}
            </Button>
          </Space>
        ) : null,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Typography.Title level={4} style={{ margin: 0 }}>
        Quốc gia
      </Typography.Title>

      {errorMessage !== null && <Alert type="error" showIcon message={errorMessage} />}

      <Alert
        type="info"
        showIcon
        message="Danh sách nạp sẵn là tập khởi đầu"
        description={
          'Hệ thống nạp sẵn các nước có giao dịch thực tế, không phải toàn bộ danh sách ISO 3166-1. '
          + 'Cần nước nào chưa có thì thêm ngay tại đây — mã quốc gia đi vào tờ khai hải quan và '
          + 'giấy chứng nhận xuất xứ, nên tên viết sai tệ hơn tên còn thiếu.'
        }
      />

      <Space wrap>
        <Input.Search
          placeholder="Tìm theo mã hoặc tên"
          allowClear
          style={{ width: 260 }}
          onSearch={(value) => {
            setPageNumber(1);
            setSearchTerm(value);
          }}
        />

        <Space size={4}>
          <Switch
            checked={includeInactive}
            onChange={(checked) => {
              setPageNumber(1);
              setIncludeInactive(checked);
            }}
          />
          <Typography.Text>Hiện cả quốc gia đã ngừng</Typography.Text>
        </Space>

        <Button icon={<ReloadOutlined />} onClick={reload}>
          Tải lại
        </Button>

        {canManage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateForm}>
            Thêm quốc gia
          </Button>
        )}
      </Space>

      <Table<Country>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={[...items]}
        loading={isLoading}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: pageNumber,
          pageSize,
          total: totalCount,
          showSizeChanger: false,
          showTotal: (total, range) =>
            `${range[0].toLocaleString('vi-VN')}–${range[1].toLocaleString('vi-VN')}`
            + ` trong ${total.toLocaleString('vi-VN')}`,
          onChange: setPageNumber,
        }}
      />

      <Modal
        title={editingCountry === null ? 'Thêm quốc gia' : `Sửa quốc gia ${editingCountry.code}`}
        open={isFormOpen}
        confirmLoading={isSaving}
        onOk={() => {
          form.submit();
        }}
        onCancel={() => {
          setIsFormOpen(false);
        }}
        okText="Lưu"
        cancelText="Hủy"
        destroyOnHidden
      >
        <Form<CountryFormValues>
          form={form}
          layout="vertical"
          requiredMark={false}
          disabled={isSaving}
          onFinish={(values) => {
            void handleSubmit(values);
          }}
        >
          <Form.Item
            name="code"
            label="Mã quốc gia"
            extra="Đúng hai chữ in hoa theo chuẩn ISO 3166-1. Ví dụ: VN, JP, DE."
            rules={[{ required: true, message: 'Chưa nhập mã quốc gia.' }]}
          >
            {/* Không sửa được mã: nó đã đi vào tờ khai hải quan và giấy chứng nhận xuất xứ. */}
            <Input disabled={editingCountry !== null} />
          </Form.Item>

          <Form.Item
            name="codeAlpha3"
            label="Mã ba chữ"
            extra="Đúng ba chữ in hoa. Một số biểu mẫu hải quan dùng mã này. Để trống nếu chưa biết."
          >
            <Input placeholder="VNM, JPN, DEU" />
          </Form.Item>

          <Form.Item
            name="name"
            label="Tên tiếng Việt"
            rules={[{ required: true, message: 'Chưa nhập tên tiếng Việt.' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="nameEn"
            label="Tên tiếng Anh"
            extra="In trên hợp đồng ngoại thương và giấy chứng nhận xuất xứ."
            rules={[{ required: true, message: 'Chưa nhập tên tiếng Anh.' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="phoneCode"
            label="Mã điện thoại"
            extra="Bắt đầu bằng dấu cộng rồi một đến bốn chữ số. Ví dụ: +84, +81, +1."
          >
            <Input placeholder="+84" />
          </Form.Item>

          <Form.Item
            name="defaultCurrencyCode"
            label="Đồng tiền thường dùng"
            extra="Chỉ là gợi ý khi lập chứng từ, không phải ràng buộc. Phải là mã đã có trong danh mục tiền tệ."
          >
            <Input placeholder="JPY" />
          </Form.Item>

          <Form.Item
            name="sortOrder"
            label="Thứ tự trình bày"
            extra="Số nhỏ nằm trên. Việt Nam để số nhỏ nhất."
            rules={[{ required: true, message: 'Chưa nhập thứ tự trình bày.' }]}
          >
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
